import { useCallback, useEffect, useRef, useState } from "react";

const MARKER = "__destyView";
const SNAPSHOT_LIMIT = 40;
interface Frame<T> { token: string; key: string; snapshot: T; depth: number }
interface Marker { owner: string; token: string; depth: number }
interface PendingClose<T> { token: string; queued?: { key: string; snapshot: T } }

interface BackNavigationOptions<T> {
  /** A view identity, excluding query characters, location updates and other live data. */
  key: string;
  snapshot: T;
  onRestore: (snapshot: T) => void;
  /** Distinguishes closing an overlay from visiting a previously opened page again. */
  isDismissal?: (previous: T, next: T) => boolean;
}

/**
 * Adds in-app views to WebView/browser history. Only an opaque marker enters
 * browser history; route geometries and other state stay in a bounded memory
 * cache. Entries older than that cache return to the base app view.
 */
export function useBackNavigation<T>({ key, snapshot, onRestore, isDismissal }: BackNavigationOptions<T>) {
  const callbacks = useRef({ onRestore, isDismissal });
  callbacks.current = { onRestore, isDismissal };
  const manager = useRef<{ update: (key: string, snapshot: T) => void; back: () => boolean } | null>(null);
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    const owner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    let sequence = 0;
    const base: Frame<T> = { token: `${owner}:${sequence++}`, key, snapshot, depth: 0 };
    const frames = new Map<string, Frame<T>>([[base.token, base]]);
    let current = base;
    let pending: PendingClose<T> | null = null;
    let restoring: string | null = null;
    let alive = true;
    const stateFor = (frame: Frame<T>) => ({
      ...(history.state && typeof history.state === "object" ? history.state : {}),
      [MARKER]: { owner, token: frame.token, depth: frame.depth } satisfies Marker,
    });
    const trim = () => {
      for (const token of frames.keys()) {
        if (frames.size <= SNAPSHOT_LIMIT) break;
        if (token !== base.token && token !== current.token && token !== pending?.token) frames.delete(token);
      }
    };
    const publish = () => { if (alive) setCanGoBack(current.depth > 0); };
    const push = (nextKey: string, nextSnapshot: T) => {
      for (const [token, frame] of frames) if (frame.depth > current.depth) frames.delete(token);
      current = { token: `${owner}:${sequence++}`, key: nextKey, snapshot: nextSnapshot, depth: current.depth + 1 };
      frames.set(current.token, current);
      history.pushState(stateFor(current), "");
      trim();
      publish();
    };
    const update = (nextKey: string, nextSnapshot: T) => {
      if (pending) {
        const target = frames.get(pending.token);
        if (target?.key === nextKey) target.snapshot = nextSnapshot;
        else pending.queued = { key: nextKey, snapshot: nextSnapshot };
        return;
      }
      if (restoring === nextKey) {
        restoring = null;
        current.snapshot = nextSnapshot;
        return;
      }
      if (current.key === nextKey) { current.snapshot = nextSnapshot; return; }
      const earlier = [...frames.values()].filter(frame => frame.depth < current.depth && frame.key === nextKey)
        .sort((a, b) => b.depth - a.depth)[0];
      const closing = callbacks.current.isDismissal
        ? callbacks.current.isDismissal(current.snapshot, nextSnapshot)
        : earlier?.depth === current.depth - 1;
      if (closing && earlier) {
        earlier.snapshot = nextSnapshot;
        pending = { token: earlier.token };
        history.go(earlier.depth - current.depth);
        return;
      }
      restoring = null;
      push(nextKey, nextSnapshot);
    };
    const pop = (event: PopStateEvent) => {
      const marker = event.state?.[MARKER] as Marker | undefined;
      if (!marker || marker.owner !== owner || !Number.isInteger(marker.depth) || marker.depth < 0) return;
      const queued = pending?.token === marker.token ? pending.queued : undefined;
      pending = null;
      current = frames.get(marker.token) ?? { ...base, token: marker.token, depth: marker.depth };
      frames.set(current.token, current);
      trim();
      if (queued) {
        // A second user action can happen before an asynchronous history.go()
        // finishes. Keep that new action instead of restoring a stale overlay.
        push(queued.key, queued.snapshot);
        return;
      }
      restoring = current.key;
      callbacks.current.onRestore(current.snapshot);
      publish();
    };
    history.replaceState(stateFor(base), "");
    window.addEventListener("popstate", pop);
    manager.current = { update, back: () => {
      if (!alive || current.depth === 0 || pending) return false;
      history.back();
      return true;
    } };
    publish();
    return () => {
      alive = false;
      manager.current = null;
      window.removeEventListener("popstate", pop);
      frames.clear();
    };
    // Initial key/snapshot seed this mounted app session; subsequent values are
    // reconciled by the effect below without reinstalling history listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { manager.current?.update(key, snapshot); }, [key, snapshot]);
  const back = useCallback(() => manager.current?.back() ?? false, []);
  return { back, canGoBack };
}
